import React, { useEffect, useMemo } from 'react';
import { TouchableOpacity } from '../../common';
import { isEmpty, isUndefined } from 'lodash';
import { FilterRoute, FiltersPayload } from '@zonofi/zono-money-store';
import { countSelectedFilters, hasSelectedSort } from './helpers';
import { useUrlDateRange } from '../../hooks';
import { showDateFilter } from '../../helper';

interface FilterLeftSectionProps {
  currentIndex: number;
  setCurrentIndex: (val: number) => void;
  filters: FiltersPayload;
  route?: FilterRoute;
  filterPage?: string;
}

export const FilterLeftSection: React.FC<FilterLeftSectionProps> = ({
  currentIndex,
  setCurrentIndex,
  filters,
  route,
  filterPage,
}) => {
  const { sortOptions = {}, listFilter = {}, treeFilter = {} } = filters;
  const { dateRangeFromUrl } = useUrlDateRange();

  const hasSortFilters = useMemo(() => !isEmpty(sortOptions), [sortOptions]);
  const hasDateRangeFilter = useMemo(
    () => !isUndefined(dateRangeFromUrl),
    [dateRangeFromUrl]
  );

  const allFilters = useMemo(() => {
    const result: string[] = [
      ...Object.keys(listFilter),
      ...Object.keys(treeFilter),
    ];

    if (hasDateRangeFilter && showDateFilter(route as FilterRoute)) {
      result.push('DateRange');
    }

    return result;
  }, [listFilter, treeFilter, hasDateRangeFilter, route]);

  const computedFilters = useMemo(() => {
    return hasSortFilters ? ['Sort', ...allFilters] : allFilters;
  }, [hasSortFilters, allFilters]);

  useEffect(() => {
    if (filterPage) {
      const index = computedFilters.findIndex(
        (filter) => filter === filterPage
      );
      const nextIndex = index === -1 ? 0 : index;
      setCurrentIndex(nextIndex);
    }
  }, [filterPage]);

  useEffect(() => {
    if (computedFilters.length === 0) return;
    if (currentIndex >= computedFilters.length) {
      setCurrentIndex(0);
    }
  }, [computedFilters.length, currentIndex]);

  return (
    <div className="filter-modal-left-box">
      {hasSortFilters && (
        <TouchableOpacity
          onPress={() => setCurrentIndex(0)}
          className={`filter-modal-left-box-item ${
            currentIndex === 0 ? 'selected-item-bg' : ''
          }`}
        >
          <span
            className={`filter-modal-left-box-text ${
              currentIndex === 0 ? 'selected-item-text' : ''
            } font-semibold`}
          >
            Sort
          </span>
          {hasSelectedSort(sortOptions) > 0 && (
            <div className="selected-count font-semibold">
              {hasSelectedSort(sortOptions)}
            </div>
          )}
        </TouchableOpacity>
      )}
      {computedFilters?.map((item, index) => {
        if (item === 'Sort') return null;
        const isActive = currentIndex === index;

        return (
          <TouchableOpacity
            key={item}
            onPress={() => setCurrentIndex(index)}
            className={`filter-modal-left-box-item ${
              isActive ? 'selected-item-bg' : ''
            }`}
          >
            <span
              className={`filter-modal-left-box-text ${
                isActive ? 'selected-item-text' : ''
              } font-semibold`}
            >
              {item}
            </span>
            {countSelectedFilters(filters, item) > 0 && (
              <div className="selected-count font-semibold">
                {countSelectedFilters(filters, item)}
              </div>
            )}
          </TouchableOpacity>
        );
      })}
    </div>
  );
};
